import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Project API functions
export const getAllProjects = async (params = {}) => {
  try {
    const response = await api.get('/projects', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getProjectById = async (id) => {
  try {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
};

export const createProject = async (projectData) => {
  try {
    const response = await api.post('/projects', projectData);
    return response.data;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const updateProject = async (id, projectData) => {
  try {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const deleteProject = async (id) => {
  try {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

export const getProjectStats = async () => {
  try {
    const response = await api.get('/projects/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getProjectDashboard = async () => {
  try {
    const response = await api.get('/projects/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching project dashboard:', error);
    throw error;
  }
};

// Master data API functions for dropdowns
export const getProjectTypes = async () => {
  try {
    const response = await api.get('/project-type-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching project types:', error);
    throw error;
  }
};

export const getProjectStatuses = async () => {
  try {
    const response = await api.get('/project-status-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching project statuses:', error);
    throw error;
  }
};

export const getProjectCategories = async () => {
  try {
    // Fetch all categories without pagination for dropdown
    const response = await api.get('/project-category-masters', { 
      params: { 
        limit: 1000, // Get all categories
        page: 1,
        status: 'Active' // Only get active categories for dropdown
      } 
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching project categories:', error);
    throw error;
  }
};

export const getPriorities = async () => {
  try {
    const response = await api.get('/priority-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching priorities:', error);
    throw error;
  }
};

export const getCustomers = async () => {
  try {
    const response = await api.get('/customer-master');
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

export const getUsers = async () => {
  try {
    // Fetch active users only, with a high limit to get all users
    const response = await api.get('/users', { 
      params: { 
        limit: 1000, 
        status: 'Active',
        isActive: true 
      } 
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const getProjectTemplates = async () => {
  try {
    const response = await api.get('/project-template-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching project templates:', error);
    throw error;
  }
};

export const getTechnologyStacks = async () => {
  try {
    const response = await api.get('/technology-stack-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching technology stacks:', error);
    throw error;
  }
};

export const getResourceRoles = async () => {
  try {
    const response = await api.get('/resource-role-masters');
    return response.data;
  } catch (error) {
    console.error('Error fetching resource roles:', error);
    throw error;
  }
};

// File upload functions
export const uploadFile = async (file, type) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', type || 'project-document');

    const response = await api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const result = response.data;
    if (result?.success && result.data) {
      result.data.filePath = result.data.filePath || result.data.filename;
      result.data.fileName = result.data.fileName || result.data.originalname;
    }
    return result;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const downloadFile = async (filePath) => {
  try {
    const response = await api.get(`/files/download?path=${encodeURIComponent(filePath)}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

// Default export with all functions
const projectService = {
  getAllProjects,
  getProjectById,
  createProject,
  updateProject,
  deleteProject,
  getProjectStats,
  getProjectDashboard,
  getProjectTypes,
  getProjectStatuses,
  getProjectCategories,
  getPriorities,
  getCustomers,
  getUsers,
  getProjectTemplates,
  getTechnologyStacks,
  getResourceRoles,
  uploadFile,
  downloadFile
};

export default projectService;
